import { defineComponent as _defineComponent } from 'vue'
import { vShow as _vShow, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createVNode as _createVNode } from "vue"

const _hoisted_1 = { id: "pegman-wrapper" }
const _hoisted_2 = {
  id: "pegman",
  class: "pegman-standing"
}
const _hoisted_3 = { id: "pegman-pano" }
const _hoisted_4 = ["src"]
const _hoisted_5 = {
  key: 1,
  id: "pegman-unavailable"
}

import { ref, onMounted, watch } from 'vue';
    import { useStore } from 'vuex';
    import * as turf from '@turf/turf';
    import StreetView from './StreetView.vue';
    import AppDialog from '@/components/main/Dialog.vue';
    import { MapEvent, MapMouseEvent, ScaleControl } from 'mapbox-gl';

    const FLOAT_OFFSET_Y = 120; // The amount the pegman floats above the cursor
    const PEGMAN_TOOLBAR = 29; // The height pegman needs to be set at to align with the bottom of the scalebar

    type Pegman = {
        dragging: boolean // Are we currently dragging pegman?
        paused: boolean // Are we holding pegman still?
        done: boolean // Have we dropped pegman?
        show: boolean // Are we showing streetview?
        lat: number
        lng: number
        bearing: number
        imageUrl: string
    }

    type LngLat = {
        lng: number;
        lat: number;
    }

    
export default /*@__PURE__*/_defineComponent({
  __name: 'Pegman',
  props: {
        mapValue: { type: Object, required: true },
        scaleControlValue: { type: Object as () => ScaleControl, required: true },
    },
  setup(__props) {

    const props = __props;

    const store = useStore();

    // Reactive state (was `data` + the returns from the old `setup()`).
    const image = ref<string | null>(null);
    const unavailable = ref(false);
    const panoLoading = ref(false);
    const streetView = ref<any>(null); // template ref to <StreetView>
    const pegman = ref<Pegman>({
        dragging: false,
        paused: false,
        done: false,
        show: false,
        lat: 0.0,
        lng: 0.0,
        bearing: 0.0,
        imageUrl: '',
    });

    // Non-reactive handles kept in the closure (reassigned by the watchers below).
    let map: any = props.mapValue.map ? props.mapValue.map : props.mapValue;
    let scaleControl: ScaleControl = props.scaleControlValue;
    let pegmanElement: HTMLElement | null = null;
    let bottomToolbarElement: HTMLElement | null = null;

    function setMapEvents() { // Get the location from the map
        map.on('mousemove', (e: MapMouseEvent) => {
            const newLocation = { lng: e.lngLat.lng, lat: e.lngLat.lat };
            if (pegman.value.lng != newLocation.lng || pegman.value.lat != newLocation.lat) {
                updateLocation({ lng: newLocation.lng, lat: newLocation.lat });
            }
        });
        map.on('zoom', () => updatePegmanStanding()); // Scalebar moves when zooming
        map.on('move', () => updatePegmanStanding()); // Scalebar moves when moving the map
    }

    function updateLocation(lngLat: LngLat) {
        pegman.value.lng = lngLat.lng;
        pegman.value.lat = lngLat.lat;
    }

    function closeStreetView() {
        if (!pegman.value.show) return;
        if (streetView.value) streetView.value.cleanUp();
        pegman.value.show = false;
    }

    function updatePegmanStanding() { // Update the style of the bottom toolbar to allign witht the scalebar
        const el = pegmanElement;
        const toolbar = bottomToolbarElement;
        if (!el || !toolbar) return;

        const scaleControlWidth = (scaleControl)._container.offsetWidth;
        const pegmanWidth = el.offsetWidth;

        // Only update pegman when we are not dragging him
        if (el.classList.contains('pegman-standing')) {
            const transformX = scaleControlWidth / 2 + pegmanWidth;
            el.style.transform = `translate(${-transformX}px, ${-PEGMAN_TOOLBAR}px)`;
        }
    }

    function isUnavailable() {
        unavailable.value = true;
        image.value = null;
        pegman.value.imageUrl = '';
        endPanoLoad();
    }

    function startPanoLoad() {
        unavailable.value = false;
        if (!panoLoading.value) {
            panoLoading.value = true;
            store.commit('SET_STARTINPROGRESS', null, { root: true });
        }
    }

    function endPanoLoad() {
        if (panoLoading.value) {
            panoLoading.value = false;
            store.commit('SET_ENDINPROGRESS', null, { root: true });
        }
    }

    onMounted(() => {
        const streetViewKey = store.getters.getStreetViewKey;
        pegmanElement = document.getElementById('pegman');
        bottomToolbarElement = document.getElementById('tbdc-bottomtoolbar');
        // Setup mouse events
        setMapEvents();
        // Setup pegman UI location
        updatePegmanStanding();

        // Defines a dragging element
        const dragElement = (elmnt: HTMLElement | null) => {
            if (!elmnt) return;

            elmnt.onmousedown = (e) => dragMouseDown(e);

            const showPopup = (e: MouseEvent) => {
                if (document.onmouseup == null) { // Timer might cause this method to fire after the onmouseup event has fired
                    return;
                }
                const src = e.target as HTMLElement;
                if (!(src instanceof HTMLCanvasElement)) { // Only show popup when we are above the map
                    return;
                }

                endPanoLoad();

                const location = pegman.value.lat + ',' + pegman.value.lng;
                const request =
                    'https://maps.googleapis.com/maps/api/streetview/metadata?location=' +
                    location +
                    '&key=' +
                    streetViewKey;
                fetch(request)
                    .then((response) => response.json())
                    .then((data) => {
                        if (pegman.value.done) return;
                        if (data.status !== 'ZERO_RESULTS') {
                            startPanoLoad();
                            const imageUrl =
                                'https://maps.googleapis.com/maps/api/streetview?size=600x300&location=' +
                                location +
                                '&key=' +
                                streetViewKey;
                            image.value = imageUrl;
                            pegman.value.imageUrl = imageUrl;
                            const a = turf.point([pegman.value.lng, pegman.value.lat]);
                            const b = turf.point([data.location.lng, data.location.lat]);
                            pegman.value.bearing = turf.bearing(b, a);
                        } else {
                            isUnavailable();
                        }
                    })
                    .catch(() => isUnavailable());
            };

            const showStreetViewMaybe = () => { // Show streetview if we have an imageUrl
                if (!pegman.value.done || pegman.value.show) return;
                pegman.value.show = pegman.value.imageUrl !== '';
                image.value = null;
                elmnt.classList.remove('pegman-moving');
                elmnt.classList.add('pegman-standing');
                updatePegmanStanding();
                elmnt.style.top = '';
                elmnt.style.left = '';
            };

            let prevTimer: any = null;

            const elementDrag = (e: MouseEvent) => { // Update element to be positioned above the cursor
                e.preventDefault();

                // Center horizontally, float above cursor
                const elmntWidth = elmnt.offsetWidth;
                elmnt.style.left = (e.clientX - elmntWidth / 2) + 'px';
                elmnt.style.top = (e.clientY - FLOAT_OFFSET_Y) + 'px';
                unavailable.value = false;
                endPanoLoad();
                image.value = null;

                // Only retrieve the data when we hold the mouse still
                if (prevTimer) clearTimeout(prevTimer);
                prevTimer = setTimeout(() => showPopup(e), 100);
            };

            const closeDragElement = () => {
                document.onmouseup = null;
                document.onmousemove = null;
                pegman.value.dragging = false;
                pegman.value.done = true;
                endPanoLoad();
                unavailable.value = false;
                showStreetViewMaybe();
            };

            const dragMouseDown = (e: MouseEvent) => { // Start dragging the element
                elmnt.classList.remove('pegman-standing');
                elmnt.classList.add('pegman-moving');
                pegman.value.dragging = true;
                pegman.value.done = false;
                pegman.value.paused = false;
                pegman.value.imageUrl = '';
                unavailable.value = false;
                if (pegman.value.show) closeStreetView();

                e = e || window.event;
                e.preventDefault();
                // Immediately center and float above cursor
                const elmntWidth = elmnt.offsetWidth;
                elmnt.style.left = (e.clientX - elmntWidth / 2) + 'px';
                elmnt.style.top = (e.clientY - FLOAT_OFFSET_Y) + 'px';
                elmnt.style.transform = 'none';

                document.onmouseup = closeDragElement;
                document.onmousemove = elementDrag;
            };
        };
        dragElement(pegmanElement);
    });

    watch(() => props.mapValue, (next) => {
        map = next;
        closeStreetView();
        setMapEvents();
    });
    watch(() => props.scaleControlValue, (next) => {
        scaleControl = next;
    });

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        (image.value)
          ? _withDirectives((_openBlock(), _createElementBlock("img", {
              key: 0,
              onLoad: endPanoLoad,
              src: image.value,
              alt: "Street View Image"
            }, null, 40, _hoisted_4)), [
              [_vShow, !panoLoading.value]
            ])
          : _createCommentVNode("", true),
        (!image.value && !panoLoading.value && unavailable.value && false)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_ctx.$t("message.unavailable")), 1))
          : _createCommentVNode("", true)
      ]),
      (!pegman.value.dragging)
        ? (_openBlock(), _createBlock(_component_v_icon, {
            key: 0,
            class: "pegman-icon"
          }, {
            default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
              _createTextVNode("mdi-human", -1)
            ]))]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (pegman.value.dragging)
        ? (_openBlock(), _createBlock(_component_v_icon, {
            key: 1,
            class: "pegman-icon"
          }, {
            default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
              _createTextVNode("mdi-human-handsup", -1)
            ]))]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    _createVNode(AppDialog, {
      show: pegman.value.show,
      onShowChanged: _cache[1] || (_cache[1] = ($event: any) => (closeStreetView()))
    }, {
      title: _withCtx(() => [...(_cache[4] || (_cache[4] = [
        _createTextVNode(" StreetView ", -1)
      ]))]),
      content: _withCtx(() => [
        (pegman.value.show)
          ? (_openBlock(), _createBlock(StreetView, {
              key: 0,
              ref_key: "streetView",
              ref: streetView,
              manValue: pegman.value,
              onClose: _cache[0] || (_cache[0] = ($event: any) => (closeStreetView()))
            }, null, 8, ["manValue"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["show"])
  ]))
}
}

})